import { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { looksLikeRichTextHtml } from '@kennisbank/shared';
import { sanitizeRichTextHtml } from '../lib/sanitize.js';

/**
 * Toont door gebruikers geschreven tekst. Eén component voor alle vier de
 * schrijfschermen (agenda, nieuws, welkomstpaneel, e-learning).
 *
 * Twee paden, want er staan drie formaten in de database:
 *   - HTML uit de editor        → gefilterd door sanitizeRichTextHtml
 *   - markdown of platte tekst  → react-markdown (remark-breaks houdt losse
 *                                 regeleinden intact, dus platte tekst met
 *                                 enters blijft leesbaar)
 *
 * Zo hoeft er niets aan bestaande data gemigreerd te worden: oude teksten
 * blijven renderen zoals nu, en zodra iemand ze in de editor opslaat zijn ze
 * HTML. Het markdown-pad kan weg zodra alles herschreven is.
 *
 * `html` schakelt het raden uit. Zet die vlag op velden die per definitie HTML
 * bevatten (e-learning-lesinhoud). Zonder de vlag valt inhoud die niet met een
 * blok-tag begint — `<span>…`, `<strong>…`, een HTML-commentaar — terug op het
 * markdown-pad, en dat gooit HTML wég in plaats van te tonen: een lege les voor
 * de cursist, en bij de eerstvolgende opslag door de beheerder permanent weg.
 * Bij een veld waarvan het formaat vastligt hoort de renderer niet te gokken.
 */
export function RichText({
  value,
  className,
  html,
}: {
  value: string;
  className?: string;
  html?: boolean;
}): JSX.Element {
  const sanitized = useMemo(
    () => (html === true || looksLikeRichTextHtml(value) ? sanitizeRichTextHtml(value) : null),
    [value, html],
  );

  const prose = `${PROSE} ${className ?? ''}`;

  if (sanitized !== null) {
    return <div className={prose} dangerouslySetInnerHTML={{ __html: sanitized }} />;
  }

  return (
    <div className={prose}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        components={{
          a: ({ children, ...props }) => (
            <a {...props} target="_blank" rel="noreferrer">
              {children}
            </a>
          ),
        }}
      >
        {value}
      </ReactMarkdown>
    </div>
  );
}

/**
 * Opmaak voor beide paden. Losse selectors in plaats van de typography-plugin,
 * zodat editor-weergave en lees-weergave gegarandeerd hetzelfde tonen (de
 * editor gebruikt dezelfde klasse).
 */
export const PROSE = [
  '[&_p]:my-2',
  '[&_h1]:mb-2 [&_h1]:mt-4 [&_h1]:text-[18px] [&_h1]:font-bold [&_h1]:text-ink-900',
  '[&_h2]:mb-2 [&_h2]:mt-4 [&_h2]:text-[16px] [&_h2]:font-bold [&_h2]:text-ink-900',
  '[&_h3]:mb-1 [&_h3]:mt-3 [&_h3]:text-[14px] [&_h3]:font-semibold [&_h3]:text-ink-900',
  '[&_strong]:font-semibold [&_strong]:text-ink-900',
  '[&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5',
  '[&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5',
  '[&_li]:my-0.5',
  '[&_blockquote]:my-2 [&_blockquote]:border-l-2 [&_blockquote]:border-brand-200 [&_blockquote]:pl-3 [&_blockquote]:text-ink-900/60',
  '[&_hr]:my-4 [&_hr]:border-brand-100',
  '[&_code]:rounded [&_code]:bg-brand-50 [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-[12px]',
  '[&_img]:my-2 [&_img]:max-w-full [&_img]:rounded-lg',
  '[&_table]:my-2 [&_table]:w-full [&_table]:border-collapse',
  '[&_td]:border [&_td]:border-brand-100 [&_td]:px-2 [&_td]:py-1',
  '[&_th]:border [&_th]:border-brand-100 [&_th]:bg-brand-50 [&_th]:px-2 [&_th]:py-1 [&_th]:text-left',
  '[&_a]:font-medium [&_a]:text-brand-700 [&_a]:underline [&_a]:decoration-brand-200 [&_a]:underline-offset-2 hover:[&_a]:decoration-brand-700',
].join(' ');
